import { formatDistanceToNow } from "date-fns";
import { Card } from "@/components/ui/card";
import { AvatarStack } from "@/components/ui/avatar-stack";
import { Skeleton } from "@/components/ui/skeleton";
import { StageChip } from "@/components/tasks/stage-ui";
import { cn } from "@/lib/utils";
import type { PublicStage, PublicTask } from "@/services/public-view.service";
import { PriorityBadge, TaskTypeChip } from "./PublicTaskBits";
import { FALLBACK_STAGE_COLOR, publicAssignees } from "./utils";

const HEADERS = ["Task", "Project", "Status", "Priority", "Assignee", "Sprint", "Updated"];

function Row({ task, stage }: { task: PublicTask; stage?: PublicStage }) {
	const assignees = publicAssignees(task);

	return (
		<tr className="border-b border-border last:border-0 hover:bg-muted-subtle transition-colors">
			<td className="px-4 py-3.5 pl-5">
				<p className="text-sm font-medium text-foreground">{task.title}</p>
				{task.task_type && (
					<div className="mt-1">
						<TaskTypeChip type={task.task_type} />
					</div>
				)}
			</td>
			<td className="px-4 py-3.5 text-xs text-muted-foreground">
				{task.project.name}
			</td>
			<td className="px-4 py-3.5 whitespace-nowrap">
				<StageChip
					label={stage?.name ?? task.status}
					color={stage?.color ?? FALLBACK_STAGE_COLOR}
				/>
			</td>
			<td className="px-4 py-3.5">
				<PriorityBadge priority={task.priority} />
			</td>
			<td className="px-4 py-3.5">
				{assignees.length > 0 ? (
					<div className="flex items-center gap-2">
						<AvatarStack profiles={assignees} size="sm" />
						<span className="text-xs text-muted-foreground truncate max-w-40">
							{assignees.map((a) => a.full_name ?? "Unnamed").join(", ")}
						</span>
					</div>
				) : (
					<span className="text-xs text-muted">Unassigned</span>
				)}
			</td>
			<td className="px-4 py-3.5 text-xs text-muted-foreground whitespace-nowrap">
				{task.sprint?.name ?? "—"}
			</td>
			<td className="px-4 py-3.5 text-xs text-muted whitespace-nowrap">
				{formatDistanceToNow(new Date(task.updated_at), { addSuffix: true })}
			</td>
		</tr>
	);
}

export function PublicTaskTable({
	tasks,
	stages,
	loading,
}: {
	tasks: PublicTask[];
	stages: PublicStage[];
	loading: boolean;
}) {
	const stageByKey = new Map(stages.map((s) => [s.key, s]));

	return (
		<Card className="p-0 overflow-hidden">
			<div className="overflow-x-auto">
				<table className="w-full min-w-[880px] text-sm">
					<thead>
						<tr className="border-b border-border bg-muted-subtle/40">
							{HEADERS.map((h, i) => (
								<th
									key={h}
									className={cn(
										"px-4 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-muted",
										i === 0 && "pl-5",
									)}
								>
									{h}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{loading
							? Array.from({ length: 8 }, (_, i) => (
									<tr key={i} className="border-b border-border last:border-0">
										<td colSpan={HEADERS.length} className="px-5 py-3.5">
											<Skeleton className="h-5 w-full" />
										</td>
									</tr>
								))
							: tasks.map((task) => (
									<Row key={task.id} task={task} stage={stageByKey.get(task.status)} />
								))}
					</tbody>
				</table>
			</div>
		</Card>
	);
}
